import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Clock, X, ChevronUp, ChevronDown } from 'lucide-react';

// Quick time options
const QUICK_TIMES = [
  { label: '6:00 AM', value: '06:00' },
  { label: '9:00 AM', value: '09:00' },
  { label: '12:00 PM', value: '12:00' },
  { label: '3:00 PM', value: '15:00' },
  { label: '6:00 PM', value: '18:00' },
  { label: '9:00 PM', value: '21:00' },
];

// Convert 24h to 12h format for display
const formatTime12h = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const h = parseInt(hours, 10);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${minutes} ${period}`;
};

// Convert 12h to 24h format
const formatTime24h = (hours, minutes, period) => {
  let h = parseInt(hours, 10);
  if (period === 'PM' && h !== 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;
  return `${h.toString().padStart(2, '0')}:${minutes}`;
};

// Create or get the portal container
const getPortalContainer = () => {
  let container = document.getElementById('picker-portal');
  if (!container) {
    container = document.createElement('div');
    container.id = 'picker-portal';
    container.style.cssText = 'position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 2147483647; pointer-events: none;';
    document.body.appendChild(container);
  }
  return container;
};

const TimePicker = ({ id, value, onChange, onComplete, required, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  
  // Parse value
  const { hours, minutes, period } = React.useMemo(() => {
    if (value) {
      const [h, m] = value.split(':');
      const hourNum = parseInt(h, 10);
      const newPeriod = hourNum >= 12 ? 'PM' : 'AM';
      const newHour = hourNum === 0 ? 12 : hourNum > 12 ? hourNum - 12 : hourNum;
      return { hours: newHour.toString(), minutes: m || '00', period: newPeriod };
    }
    return { hours: '12', minutes: '00', period: 'AM' };
  }, [value]);

  // Local state for picker - initialized from parsed value
  const [localHours, setLocalHours] = useState(() => hours);
  const [localMinutes, setLocalMinutes] = useState(() => minutes);
  const [localPeriod, setLocalPeriod] = useState(() => period);

  // Reset local state when popup opens
  const openPicker = () => {
    setLocalHours(hours);
    setLocalMinutes(minutes);
    setLocalPeriod(period);
    setIsOpen(true);
  };

  // Close on escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const displayValue = value ? formatTime12h(value) : '';

  const incrementHour = () => {
    const h = parseInt(localHours, 10);
    setLocalHours((h % 12 + 1).toString());
  };

  const decrementHour = () => {
    const h = parseInt(localHours, 10);
    setLocalHours((h === 1 ? 12 : h - 1).toString());
  };

  const incrementMinute = () => {
    const m = parseInt(localMinutes, 10);
    setLocalMinutes(((m + 5) % 60).toString().padStart(2, '0'));
  };

  const decrementMinute = () => {
    const m = parseInt(localMinutes, 10);
    setLocalMinutes(((m - 5 + 60) % 60).toString().padStart(2, '0'));
  };

  const togglePeriod = () => {
    setLocalPeriod(localPeriod === 'AM' ? 'PM' : 'AM');
  };

  const handleQuickSelect = (timeValue) => {
    onChange(timeValue);
    if (onComplete) onComplete();
    setIsOpen(false);
  };

  const handleConfirm = () => {
    const newValue = formatTime24h(localHours, localMinutes, localPeriod);
    onChange(newValue);
    if (onComplete) onComplete();
    setIsOpen(false);
  };

  const closePicker = () => {
    setIsOpen(false);
  };

  // Time picker popup
  const timePickerPopup = isOpen && ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'auto',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) closePicker();
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          maxWidth: '320px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          padding: '16px',
          borderBottom: '1px solid #e5e7eb' 
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>Select Time</h3>
          <button
            type="button"
            onClick={closePicker}
            style={{ 
              padding: '8px', 
              borderRadius: '50%', 
              border: 'none', 
              background: 'transparent', 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X style={{ width: '20px', height: '20px', color: '#6b7280' }} />
          </button>
        </div>

        {/* Time picker content */}
        <div style={{ padding: '20px' }}>
          {/* Time display and spinners */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '20px' }}>
            {/* Hours */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <button
                type="button"
                onClick={incrementHour}
                style={{ padding: '8px', border: 'none', background: 'transparent', cursor: 'pointer' }}
              >
                <ChevronUp style={{ width: '24px', height: '24px', color: '#0d9488' }} />
              </button>
              <div style={{ 
                width: '60px', 
                height: '50px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                fontSize: '28px',
                fontWeight: '700',
                color: '#111827',
                backgroundColor: '#f3f4f6',
                borderRadius: '8px'
              }}>
                {localHours.padStart(2, '0')}
              </div>
              <button
                type="button"
                onClick={decrementHour}
                style={{ padding: '8px', border: 'none', background: 'transparent', cursor: 'pointer' }}
              >
                <ChevronDown style={{ width: '24px', height: '24px', color: '#0d9488' }} />
              </button>
            </div>

            <span style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>:</span>

            {/* Minutes */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <button
                type="button"
                onClick={incrementMinute}
                style={{ padding: '8px', border: 'none', background: 'transparent', cursor: 'pointer' }}
              >
                <ChevronUp style={{ width: '24px', height: '24px', color: '#0d9488' }} />
              </button>
              <div style={{ 
                width: '60px', 
                height: '50px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                fontSize: '28px',
                fontWeight: '700',
                color: '#111827',
                backgroundColor: '#f3f4f6',
                borderRadius: '8px'
              }}>
                {localMinutes}
              </div>
              <button
                type="button"
                onClick={decrementMinute}
                style={{ padding: '8px', border: 'none', background: 'transparent', cursor: 'pointer' }}
              >
                <ChevronDown style={{ width: '24px', height: '24px', color: '#0d9488' }} />
              </button>
            </div>

            {/* AM/PM */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <button
                type="button"
                onClick={togglePeriod}
                style={{ padding: '8px', border: 'none', background: 'transparent', cursor: 'pointer' }}
              >
                <ChevronUp style={{ width: '24px', height: '24px', color: '#0d9488' }} />
              </button>
              <div style={{ 
                width: '60px', 
                height: '50px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                fontSize: '20px',
                fontWeight: '700',
                color: 'white',
                backgroundColor: '#0d9488',
                borderRadius: '8px'
              }}>
                {localPeriod}
              </div>
              <button
                type="button"
                onClick={togglePeriod}
                style={{ padding: '8px', border: 'none', background: 'transparent', cursor: 'pointer' }}
              >
                <ChevronDown style={{ width: '24px', height: '24px', color: '#0d9488' }} />
              </button>
            </div>
          </div>

          {/* Quick select */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>Quick Select</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
              {QUICK_TIMES.map((time) => (
                <button
                  key={time.value}
                  type="button"
                  onClick={() => handleQuickSelect(time.value)}
                  style={{
                    padding: '10px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#374151',
                  }}
                >
                  {time.label}
                </button>
              ))}
            </div>
          </div>

          {/* Confirm button */}
          <button
            type="button"
            onClick={handleConfirm}
            style={{
              width: '100%',
              padding: '14px',
              border: 'none',
              borderRadius: '10px',
              background: 'linear-gradient(to right, #0d9488, #0f766e)',
              color: 'white',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            ✓ Select {localHours}:{localMinutes} {localPeriod}
          </button>
        </div>
      </div>
    </div>,
    getPortalContainer()
  );

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div
        data-testid="time-picker-trigger"
        className="flex h-11 sm:h-10 w-full items-center gap-2 rounded-md border border-gray-300 dark:border-border bg-white dark:bg-card px-3 py-2 shadow-sm cursor-pointer hover:border-teal-400 hover:bg-teal-50/30 transition-colors active:bg-teal-50"
        style={{ fontSize: '16px' }}
        onClick={openPicker}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') openPicker();
        }}
      >
        <Clock className="w-4 h-4 text-gray-400 dark:text-muted-foreground flex-shrink-0" />
        <span className={`flex-1 ${displayValue ? 'text-gray-900 dark:text-foreground' : 'text-gray-400 dark:text-muted-foreground'}`}>
          {displayValue || 'Select time'}
        </span>
      </div>

      {timePickerPopup}
    </div>
  );
};

export default TimePicker;
